import { useState } from "react";
import Icon1 from "@assets/icons/Icon";
import TextField from "@components/ui/textfields/TextField";
import DropDown from "@components/ui/dropdown/DropDown";
import { Switch } from "@components/ui/selection/Switch";
import { PARTY_TYPE_OPTIONS } from "@constants/clientTypes";
import { navigateTo } from "context/navigation.service";
import type { VehiclePartyBlock } from "types/client_vehicle";

// Shared editor for a client vehicle's three carte-grise parties
// (owner/loueur, société, conducteur) plus the is_fleet flag. Used by the
// Réglages → Véhicules clients review modal AND the dossier-detail vehicle modal
// so both stay in sync.

export type PartyKey = "owner" | "company" | "user";

export const PARTY_META: Record<PartyKey, { title: string; hint: string }> = {
  owner: { title: "Propriétaire (loueur)", hint: "Titulaire de la carte grise" },
  company: { title: "Société", hint: "Entreprise rattachée" },
  user: { title: "Utilisateur (conducteur)", hint: "Conducteur opérationnel" },
};

export function isBlockEmpty(b?: VehiclePartyBlock | null): boolean {
  if (!b) return true;
  return !(b.nom || b.telephone || b.email || b.adresse || b.code_postal || b.ville || b.type_client || b.dossier_id);
}

export function PartyBlockEditor({
  which,
  value,
  onChange,
}: {
  which: PartyKey;
  value: VehiclePartyBlock | null | undefined;
  onChange: (next: VehiclePartyBlock | null) => void;
}) {
  const meta = PARTY_META[which];
  const empty = isBlockEmpty(value);
  const [open, setOpen] = useState(!empty);
  const b = value || {};

  const set = (patch: Partial<VehiclePartyBlock>) => onChange({ ...b, ...patch });

  return (
    <div className="rounded-xl border border-neutral-200">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex flex-row items-center gap-2 px-3 py-2.5 text-left"
      >
        <span className={`transition-transform ${open ? "rotate-90" : ""}`}>
          <Icon1 name="right" size={16} />
        </span>
        <span className="text-h4 font-semibold text-neutral-800">{meta.title}</span>
        {empty ? (
          <span className="text-h5 text-neutral-400 ml-1">vide</span>
        ) : (
          <span className="text-h5 text-neutral-500 ml-1 truncate">{b.nom || "—"}</span>
        )}
        {!empty && (
          <span
            className="ml-auto text-h5 text-red-500 hover:text-red-700"
            onClick={(e) => {
              e.stopPropagation();
              onChange(null);
            }}
          >
            Vider
          </span>
        )}
      </button>
      {open && (
        <div className="flex flex-col gap-2 px-3 pb-3">
          <div className="text-h5 text-neutral-400 -mt-1">{meta.hint}</div>
          <div className="flex flex-row gap-2">
            <div className="flex-1">
              <TextField label="Nom" value={b.nom || ""} onChange={(v) => set({ nom: v })} />
            </div>
            <div className="flex-1">
              <DropDown
                label="Type"
                options={PARTY_TYPE_OPTIONS}
                value={b.type_client || ""}
                onSelect={(v) => set({ type_client: String(v) || undefined })}
              />
            </div>
          </div>
          <div className="flex flex-row gap-2">
            <div className="flex-1">
              <TextField label="Téléphone" value={b.telephone || ""} onChange={(v) => set({ telephone: v })} />
            </div>
            <div className="flex-1">
              <TextField label="Email" value={b.email || ""} onChange={(v) => set({ email: v })} />
            </div>
          </div>
          <TextField label="Adresse" value={b.adresse || ""} onChange={(v) => set({ adresse: v })} />
          <div className="flex flex-row gap-2">
            <div className="flex-1">
              <TextField label="Code postal" value={b.code_postal || ""} onChange={(v) => set({ code_postal: v })} />
            </div>
            <div className="flex-1">
              <TextField label="Ville" value={b.ville || ""} onChange={(v) => set({ ville: v })} />
            </div>
          </div>
          {b.dossier_id && (
            <button
              type="button"
              onClick={() => navigateTo({ route: `/dossier/${b.dossier_id}` })}
              className="flex flex-row items-center gap-1 text-h5 text-indigo-500 hover:text-indigo-700 w-fit"
            >
              <Icon1 name="right" size={12} />
              Ouvrir la fiche du dossier lié
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export interface VehicleParties {
  is_fleet: boolean;
  owner: VehiclePartyBlock | null;
  company: VehiclePartyBlock | null;
  user: VehiclePartyBlock | null;
}

/** is_fleet switch + the three party editors. Controlled via `value`/`onChange`. */
export default function VehiclePartiesEditor({
  value,
  onChange,
}: {
  value: VehicleParties;
  onChange: (next: VehicleParties) => void;
}) {
  const patch = (p: Partial<VehicleParties>) => onChange({ ...value, ...p });
  return (
    <>
      <div className="flex flex-row items-center gap-3 py-1">
        <Switch checked={value.is_fleet} onChange={(v) => patch({ is_fleet: v })} />
        <span className="text-h4 font-medium text-neutral-700">Véhicule de flotte / loueur</span>
      </div>
      <div className="flex flex-col gap-2">
        <span className="text-h4 font-semibold text-neutral-500">Parties</span>
        <PartyBlockEditor which="owner" value={value.owner} onChange={(v) => patch({ owner: v })} />
        <PartyBlockEditor which="company" value={value.company} onChange={(v) => patch({ company: v })} />
        <PartyBlockEditor which="user" value={value.user} onChange={(v) => patch({ user: v })} />
      </div>
    </>
  );
}
